// pages/tasks/EditOwnTicketModal.jsx
//
// Phase 66: a Data Analyst edits her OWN open Own Ticket.
//   mode="full" -- My Tickets -> Own Tickets "Edit": title, description,
//                  schools, category, subcategories, TAT
//   mode="tat"  -- Own Ticket modal -> Recent TAT Entries "Edit": TAT only
//
// GET/PUT /tasks/:id/own-ticket (services/dataAnalystOwnTicketService.js)
// enforce everything server-side: Data Analyst role, ownership
// (created_by = assigned_to = caller), open status, allowed fields, and the
// fixed High/Medium/Low TAT. Completed/cancelled tickets come back with
// editable=false and are shown read-only here.
//
// TAT keys are sent only when the TAT selection actually changed, so saving
// a title change never touches an older ticket's historical TAT.
import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../api";
import MultiSelectChips from "../../components/MultiSelectChips";
import DataAnalystTatPicker from "./DataAnalystTatPicker";
import { NEW_REQUIREMENT, formatTat, levelLabel, tatErrors, tatPayload } from "./dataAnalystTat";
import "./TicketModalTheme.css";

const EMPTY_TAT = { level: "", value: "", unit: "" };

const toOptions = (rows) => (rows || []).map((r) => ({ value: String(r.id), label: r.name }));

// Keeps already-selected values visible as chips even when the master list
// no longer offers them (e.g. a deactivated school).
const withSelected = (options, selected) => {
  const known = new Set(options.map((o) => o.value));
  return [...options, ...(selected || []).filter((s) => !known.has(String(s.id))).map((s) => ({ value: String(s.id), label: s.name }))];
};

function tatChanged(initial, next) {
  if (!initial) return !!next.level;
  if (initial.level !== next.level) return true;
  if (next.level !== NEW_REQUIREMENT) return false;
  return String(initial.value) !== String(next.value).trim() || initial.unit !== next.unit;
}

function todayDateString() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

// "2026-09-02 00:21:00" / "2026-09-02T00:21" -> { date, time } (local wall
// clock, no timezone conversion -- same as CompleteTicketModal).
function splitLocalDateTime(value) {
  const match = String(value || "").match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  return match ? { date: match[1], time: match[2] } : { date: "", time: "" };
}

// `completionTask` (optional, My Tickets -> Own Tickets): the list row of a
// COMPLETED own ticket. Its ticket details are locked server-side (409
// OWN_TICKET_LOCKED), so they are shown read-only in the same layout as
// Own Ticket create, and the completion details (date, time, resolution,
// remarks) are corrected here through the existing PUT /tasks/:id/complete
// -- the same call the old "Edit Completion Details" modal made.
export default function EditOwnTicketModal({ show, taskId, mode = "full", completionTask = null, onClose, onSaved }) {
  const isTatOnly = mode === "tat";
  const [completion, setCompletion] = useState({ date: "", time: "", resolution: "", remarks: "" });
  // TEMPORARY backfill (April -> now): an OPEN own ticket can be given its
  // actual completed date here, and is then marked completed through the
  // existing PUT /tasks/:id/complete. Optional -- left empty, Save behaves
  // exactly as before.
  const [backfill, setBackfill] = useState({ date: "", time: "", resolution: "" });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [ticket, setTicket] = useState(null);
  const [form, setForm] = useState(null);
  const [initialTat, setInitialTat] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [schools, setSchools] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const loadSubCategories = async (categoryId) => {
    if (!categoryId) return setSubCategories([]);
    try {
      const res = await api.get(`/subcategories/technician-owned/${categoryId}`);
      setSubCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Fetch owned subcategories error:", err);
      setSubCategories([]);
    }
  };

  useEffect(() => {
    if (!show || !taskId) return;
    let cancelled = false;
    setTicket(null);
    setForm(null);
    setSubmitted(false);
    setLoadError("");
    const done = splitLocalDateTime(completionTask?.completed_at);
    setCompletion({
      date: done.date || todayDateString(),
      time: done.time || "",
      resolution: completionTask?.completion_remark || "",
      remarks: "",
    });
    setBackfill({ date: "", time: "", resolution: "" });
    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/tasks/${taskId}/own-ticket`);
        if (cancelled) return;
        const data = res.data?.data || res.data;
        const tat = data.tat
          ? { level: String(data.tat.level || ""), value: data.tat.value != null ? String(data.tat.value) : "", unit: String(data.tat.unit || "") }
          : EMPTY_TAT;
        setTicket(data);
        setInitialTat(data.tat ? tat : null);
        setForm({
          title: data.title || "",
          description: data.description || "",
          category_id: data.category_id != null ? String(data.category_id) : "",
          sub_category_ids: (data.sub_category_ids || []).map(String),
          school_ids: (data.school_ids || []).map(String),
          tat,
        });
        if (!isTatOnly) {
          const [cats, schoolRes] = await Promise.all([
            api.get("/categories/technician-owned").catch(() => ({ data: [] })),
            api.get("/schools").catch(() => ({ data: [] })),
          ]);
          if (cancelled) return;
          setCategories(Array.isArray(cats.data) ? cats.data : []);
          setSchools(Array.isArray(schoolRes.data) ? schoolRes.data : []);
          await loadSubCategories(data.category_id);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Load Own Ticket error:", err);
        setLoadError(
          err.response?.status === 404
            ? "This ticket was not found or is not your Own Ticket."
            : err.response?.data?.message || err.response?.data?.error || "Could not load the ticket."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, taskId, isTatOnly]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const handleCategoryChange = async (categoryId) => {
    // Subcategories belong to exactly one Category, so a Category change
    // clears every previous selection.
    set({ category_id: categoryId, sub_category_ids: [] });
    await loadSubCategories(categoryId);
  };

  const editable = !!ticket?.editable;
  // TAT can change only on an OPEN ticket. A completed one keeps its TAT
  // (tat_editable=false from the API) so its "completed within TAT" result
  // never moves; an API without the flag falls back to `editable`.
  const tatEditable = ticket ? (ticket.tat_editable ?? editable) : false;
  // A completed own ticket opened from My Tickets: details editable, TAT
  // locked, completion details editable too.
  const isCompletionEdit = !isTatOnly && !!ticket && !!completionTask
    && String(ticket.status || completionTask.status || "").toLowerCase() === "completed";
  const completionValid = !!completion.date && !!completion.resolution.trim();
  const errors = form ? tatErrors(form.tat) : {};
  const tatValid = form && !errors.level && !errors.value && !errors.unit;
  // Schools are optional (all may be removed); subcategories stay required.
  const fieldsValid = isTatOnly || (form && form.title.trim() && form.category_id && form.sub_category_ids.length);
  const showBackfill = !isTatOnly && !!ticket && !isCompletionEdit && tatEditable;
  const backfillValid = !backfill.date || !!backfill.resolution.trim();
  const canSave = isCompletionEdit
    ? completionValid && (!editable || !!fieldsValid) && !saving
    : editable && !!form && tatValid && !!fieldsValid && backfillValid && !saving;

  // Only the details that actually changed are sent to PUT
  // /tasks/:id/own-ticket (a partial update) -- an untouched field is never
  // re-submitted, so it can't fail validation it already passed. A new
  // Category always carries its Subcategories (the API requires both).
  // Never TAT keys here.
  const sameIds = (a, b) => JSON.stringify([...a].map(Number).sort((x, y) => x - y)) === JSON.stringify([...b].map(Number).sort((x, y) => x - y));
  const changedDetails = () => {
    if (!ticket || !form) return {};
    const body = {};
    if (form.title.trim() !== String(ticket.title || "").trim()) body.title = form.title.trim();
    if (form.description.trim() !== String(ticket.description || "").trim()) body.description = form.description.trim();
    const categoryChanged = Number(form.category_id) !== Number(ticket.category_id);
    if (categoryChanged) body.category_id = Number(form.category_id);
    if (categoryChanged || !sameIds(form.sub_category_ids, ticket.sub_category_ids || [])) {
      body.sub_category_ids = form.sub_category_ids.map(Number);
    }
    if (!sameIds(form.school_ids, ticket.school_ids || [])) body.school_ids = form.school_ids.map(Number);
    return body;
  };
  const apiErrorText = (err, fallback) => {
    const data = err.response?.data || {};
    return data.message || data.error || (err.response ? `${fallback} (HTTP ${err.response.status})` : `${fallback} -- could not reach the server.`);
  };

  const saveCompletion = async () => {
    try {
      setSaving(true);
      // Details first (only when something changed), then the completion
      // details -- each through its existing endpoint.
      const details = editable ? changedDetails() : {};
      if (Object.keys(details).length) await api.put(`/tasks/${taskId}/own-ticket`, details);
      const res = await api.put(`/tasks/${taskId}/complete`, {
        resolution: completion.resolution.trim(),
        remarks: completion.remarks.trim() || undefined,
        completion_date: completion.date,
        completion_time: completion.time || undefined,
      });
      toast.success("Ticket updated");
      onSaved?.(res.data);
      onClose?.();
    } catch (err) {
      console.error("Update completed Own Ticket error:", err);
      toast.error(apiErrorText(err, "Failed to update ticket"));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (!canSave) return;
    if (isCompletionEdit) return saveCompletion();
    const body = isTatOnly ? {} : changedDetails();
    if (tatEditable && (isTatOnly || tatChanged(initialTat, form.tat))) Object.assign(body, tatPayload(form.tat));
    const markCompleted = showBackfill && !!backfill.date;
    if (!Object.keys(body).length && !markCompleted) {
      toast.info("No changes to save.");
      onClose?.();
      return;
    }
    try {
      setSaving(true);
      // Details/TAT first -- a completed ticket's TAT is locked afterwards.
      let res = Object.keys(body).length ? await api.put(`/tasks/${taskId}/own-ticket`, body) : null;
      if (markCompleted) {
        res = await api.put(`/tasks/${taskId}/complete`, {
          resolution: backfill.resolution.trim(),
          completion_date: backfill.date,
          completion_time: backfill.time || undefined,
        });
      }
      toast.success(markCompleted ? "Ticket marked completed" : "Ticket updated");
      onSaved?.(res.data?.data || res.data);
      onClose?.();
    } catch (err) {
      console.error("Update Own Ticket error:", err);
      toast.error(apiErrorText(err, "Failed to update ticket"));
    } finally {
      setSaving(false);
    }
  };

  const title = isTatOnly ? "Edit TAT" : "Edit Own Ticket";

  return (
    <Modal
      show={show}
      onHide={() => !saving && onClose?.()}
      centered
      scrollable
      className={`unit-modal ticket-modal-theme own-ticket-edit${isTatOnly ? "" : " own-ticket-modal"}`}
      backdropClassName="own-ticket-quickadd-backdrop"
      aria-labelledby="own-ticket-edit-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="own-ticket-edit-title">{title}{ticket ? ` — #${ticket.id}` : ""}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-3"><Spinner animation="border" size="sm" /> Loading...</div>
        )}
        {!loading && loadError && <div className="text-danger">{loadError}</div>}
        {!loading && form && (
          <div className="row g-3">
            {isCompletionEdit ? (
              <div className="col-12">
                <div className="alert alert-info mb-0 own-ticket-edit-note" role="status">
                  This ticket is completed. You can edit its details and completion details; its TAT stays as it was.
                </div>
              </div>
            ) : !editable && (
              <div className="col-12">
                <div className="alert alert-secondary mb-0" role="status">
                  This ticket is {ticket.status || "closed"} and can no longer be edited.
                </div>
              </div>
            )}

            {isTatOnly && (
              <div className="col-12">
                <div className="fw-semibold">{ticket.title}</div>
              </div>
            )}

            {!isTatOnly && (
              <>
                <div className="col-12">
                  <Form.Group controlId="own-ticket-edit-title-input">
                    <Form.Label>Title <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      value={form.title}
                      disabled={!editable}
                      isInvalid={submitted && !form.title.trim()}
                      onChange={(e) => set({ title: e.target.value })}
                    />
                    <Form.Control.Feedback type="invalid">Title is required.</Form.Control.Feedback>
                  </Form.Group>
                </div>
                <div className="col-12">
                  <Form.Group controlId="own-ticket-edit-description">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={form.description}
                      disabled={!editable}
                      onChange={(e) => set({ description: e.target.value })}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group>
                    <Form.Label htmlFor="own-ticket-edit-schools">Schools</Form.Label>
                    <MultiSelectChips
                      inputId="own-ticket-edit-schools"
                      options={withSelected(toOptions(schools), ticket.schools)}
                      value={form.school_ids}
                      onChange={(ids) => set({ school_ids: ids })}
                      placeholder="Search schools..."
                      disabled={!editable}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group controlId="own-ticket-edit-category">
                    <Form.Label>Category <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={form.category_id}
                      disabled={!editable}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                    >
                      <option value="">Select Category</option>
                      {withSelected(toOptions(categories), ticket.category ? [ticket.category] : []).map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-12">
                  <Form.Group>
                    <Form.Label htmlFor="own-ticket-edit-subcategories">Subcategories <span className="text-danger">*</span></Form.Label>
                    <MultiSelectChips
                      inputId="own-ticket-edit-subcategories"
                      options={withSelected(
                        toOptions(subCategories),
                        String(form.category_id) === String(ticket.category_id) ? ticket.sub_categories : []
                      )}
                      value={form.sub_category_ids}
                      onChange={(ids) => set({ sub_category_ids: ids })}
                      placeholder={form.category_id ? "Search subcategories..." : "Select a Category first"}
                      disabled={!editable || !form.category_id}
                      isInvalid={submitted && !form.sub_category_ids.length}
                    />
                    {submitted && !form.sub_category_ids.length && <div className="text-danger small mt-1">Select at least one subcategory.</div>}
                  </Form.Group>
                </div>
              </>
            )}

            <div className="col-12">
              {initialTat && tatEditable && (
                <div className="own-ticket-tat-current" data-testid="own-ticket-edit-current-tat">
                  Current TAT: {levelLabel(initialTat.level)} · {formatTat(initialTat.value, initialTat.unit)}
                </div>
              )}
              {tatEditable ? (
                <DataAnalystTatPicker
                  idPrefix="own-ticket-edit-tat"
                  current={initialTat}
                  value={form.tat}
                  onChange={(tat) => set({ tat })}
                  showErrors={submitted}
                />
              ) : !isTatOnly ? (
                // Completed / closed ticket: the TAT it ran with, in the same
                // Applied TAT panel the create form shows -- never editable.
                <div className="own-ticket-tat-section">
                  <div className="own-ticket-tat-title">TAT Level <span className="own-ticket-tat-locked">Locked</span></div>
                  {initialTat ? (
                    <div className="own-ticket-tat-applied" data-testid="own-ticket-edit-locked-tat">
                      <div className="own-ticket-tat-applied-title">Applied TAT</div>
                      <div className="own-ticket-tat-applied-level">{levelLabel(initialTat.level)}</div>
                      <div className="own-ticket-tat-applied-value">{formatTat(initialTat.value, initialTat.unit)}</div>
                    </div>
                  ) : (
                    <div className="own-ticket-tat-applied is-empty" data-testid="own-ticket-edit-locked-tat">
                      <div className="own-ticket-tat-applied-title">Applied TAT</div>
                      <div className="own-ticket-tat-applied-value">No TAT level was recorded for this ticket.</div>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {showBackfill && (
              <div className="col-12">
                <fieldset className="own-ticket-tat-section own-ticket-completion-section" data-testid="own-ticket-edit-backfill">
                  <legend className="own-ticket-tat-title">Task Completed Date</legend>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Form.Group controlId="own-ticket-edit-backfill-date">
                        <Form.Label>Completed Date</Form.Label>
                        <Form.Control
                          type="date"
                          max={todayDateString()}
                          value={backfill.date}
                          disabled={!editable}
                          onChange={(e) => setBackfill((b) => ({ ...b, date: e.target.value }))}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="own-ticket-edit-backfill-time">
                        <Form.Label>Completed Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={backfill.time}
                          disabled={!editable || !backfill.date}
                          onChange={(e) => setBackfill((b) => ({ ...b, time: e.target.value }))}
                        />
                      </Form.Group>
                    </div>
                    {backfill.date && (
                      <div className="col-12">
                        <Form.Group controlId="own-ticket-edit-backfill-resolution">
                          <Form.Label>Resolution / Work Done <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Describe what was done to resolve this ticket"
                            value={backfill.resolution}
                            isInvalid={submitted && !backfill.resolution.trim()}
                            onChange={(e) => setBackfill((b) => ({ ...b, resolution: e.target.value }))}
                          />
                          <Form.Control.Feedback type="invalid">Resolution is required to mark the ticket completed.</Form.Control.Feedback>
                        </Form.Group>
                      </div>
                    )}
                  </div>
                  <Form.Text className="text-muted d-block mt-2">
                    Optional. Set it to mark this ticket completed on that date (cannot be in the future or before the ticket was created).
                  </Form.Text>
                </fieldset>
              </div>
            )}

            {isCompletionEdit && (
              <div className="col-12">
                <fieldset className="own-ticket-tat-section own-ticket-completion-section">
                  <legend className="own-ticket-tat-title">Completion Details</legend>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <Form.Group controlId="own-ticket-edit-completion-date">
                        <Form.Label>Completion Date <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="date"
                          max={todayDateString()}
                          value={completion.date}
                          isInvalid={submitted && !completion.date}
                          onChange={(e) => setCompletion((c) => ({ ...c, date: e.target.value }))}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="own-ticket-edit-completion-time">
                        <Form.Label>Completion Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={completion.time}
                          onChange={(e) => setCompletion((c) => ({ ...c, time: e.target.value }))}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="own-ticket-edit-resolution">
                        <Form.Label>Resolution / Work Done <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="Describe what was done to resolve this ticket"
                          value={completion.resolution}
                          isInvalid={submitted && !completion.resolution.trim()}
                          onChange={(e) => setCompletion((c) => ({ ...c, resolution: e.target.value }))}
                        />
                        <Form.Control.Feedback type="invalid">Resolution is required.</Form.Control.Feedback>
                      </Form.Group>
                    </div>
                    <div className="col-md-6">
                      <Form.Group controlId="own-ticket-edit-remarks">
                        <Form.Label>Remarks</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="Optional additional remarks"
                          value={completion.remarks}
                          onChange={(e) => setCompletion((c) => ({ ...c, remarks: e.target.value }))}
                        />
                      </Form.Group>
                    </div>
                  </div>
                  <Form.Text className="text-muted d-block mt-2">Completion date cannot be in the future.</Form.Text>
                </fieldset>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={() => onClose?.()} disabled={saving}>
          Cancel
        </Button>
        <Button
          className="unit-btn unit-btn-primary"
          onClick={handleSave}
          disabled={isCompletionEdit ? !canSave : !editable || !form || saving}
        >
          {saving ? "Saving..." : isCompletionEdit ? "Save Changes" : "Save"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
